import { useState, useEffect } from 'react';
import { telegram as telegramApi } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Phone, CheckCircle, ExternalLink, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function TelegramSetup({ hotelId }) {
  const [form, setForm] = useState({
    bot_token: '',
    chat_id: '',
    housekeeping_telegram_chat_id: '',
    maintenance_telegram_chat_id: '',
    front_desk_telegram_chat_id: '',
    manager_telegram_chat_id: '',
    enabled: false
  });
  const [status, setStatus] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showSetup, setShowSetup] = useState(false);

  useEffect(() => {
    if (!hotelId) return;
    telegramApi.status(hotelId).then(({ data }) => {
      setStatus(data);
      setForm(p => ({
        ...p,
        chat_id: data.chat_id || '',
        housekeeping_telegram_chat_id: data.housekeeping_telegram_chat_id || '',
        maintenance_telegram_chat_id: data.maintenance_telegram_chat_id || '',
        front_desk_telegram_chat_id: data.front_desk_telegram_chat_id || '',
        manager_telegram_chat_id: data.manager_telegram_chat_id || '',
        enabled: data.enabled || false
      }));
    }).catch(() => {}).finally(() => setLoading(false));
  }, [hotelId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await telegramApi.setup(hotelId, form);
      toast.success(data.message || 'Telegram settings saved successfully');
      setStatus({
        enabled: form.enabled,
        has_bot_token: !!form.bot_token,
        has_chat_id: !!form.chat_id,
        chat_id: form.chat_id,
        housekeeping_telegram_chat_id: form.housekeeping_telegram_chat_id,
        maintenance_telegram_chat_id: form.maintenance_telegram_chat_id,
        front_desk_telegram_chat_id: form.front_desk_telegram_chat_id,
        manager_telegram_chat_id: form.manager_telegram_chat_id
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to setup Telegram');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    if (!form.chat_id) {
      toast.error('Please enter a chat ID first');
      return;
    }
    try {
      await telegramApi.test(hotelId);
      toast.success('Test message sent! Check your Telegram');
    } catch (e) {
      toast.error('Failed to send test message');
    }
  };

  if (loading) {
    return <Card><CardContent className="p-8 text-center">Loading...</CardContent></Card>;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Phone className="w-5 h-5" />
          Telegram Integration
        </CardTitle>
        <CardDescription>
          Connect your Telegram bot to receive booking notifications and service request alerts
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {!showSetup ? (
          <div className="space-y-4">
            <Alert>
              <Phone className="h-4 w-4" />
              <AlertTitle>Configure Telegram Notifications</AlertTitle>
              <AlertDescription>
                Set up different Telegram chats for bookings and department-specific service requests
              </AlertDescription>
            </Alert>

            {/* Main Bot Configuration */}
            <div className="space-y-4 p-4 border rounded-lg">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-sm">Main Bot Configuration</h3>
                {status?.enabled && <Badge variant="success">Active</Badge>}
              </div>
              <div className="grid gap-4">
                <div className="space-y-2">
                  <Label htmlFor="botToken">Bot Token</Label>
                  <Input
                    id="botToken"
                    type="password"
                    placeholder="1234567890:ABCdefGHIjklMNOpqrsTUVwxyz"
                    value={form.bot_token}
                    onChange={(e) => setForm({ ...form, bot_token: e.target.value })}
                    data-testid="telegram-bot-token-input"
                  />
                  <p className="text-xs text-muted-foreground">Get from @BotFather on Telegram</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="chatId">Default Chat ID (Bookings & General)</Label>
                  <Input
                    id="chatId"
                    placeholder="123456789"
                    value={form.chat_id}
                    onChange={(e) => setForm({ ...form, chat_id: e.target.value })}
                    data-testid="telegram-chat-id-input"
                  />
                  <p className="text-xs text-muted-foreground">Send /start to your bot, then check @userinfobot</p>
                </div>
              </div>
            </div>

            <Separator />

            {/* Department-Specific Chats */}
            <div className="space-y-4 p-4 border rounded-lg bg-muted/30">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-sm">Department Service Request Alerts</h3>
                <Badge variant="secondary">Optional</Badge>
              </div>
              <p className="text-xs text-muted-foreground">
                Configure separate Telegram chats to receive targeted alerts for each department
              </p>

              <div className="grid gap-4">
                <div className="space-y-2">
                  <Label htmlFor="housekeepingChatId" className="flex items-center gap-2">
                    🧹 Housekeeping Chat ID
                  </Label>
                  <Input
                    id="housekeepingChatId"
                    placeholder="123456789 (optional)"
                    value={form.housekeeping_telegram_chat_id}
                    onChange={(e) => setForm({ ...form, housekeeping_telegram_chat_id: e.target.value })}
                    data-testid="housekeeping-chat-id-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Receives: Towel requests, cleaning alerts, checkout notifications
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="maintenanceChatId" className="flex items-center gap-2">
                    🔧 Maintenance Chat ID
                  </Label>
                  <Input
                    id="maintenanceChatId"
                    placeholder="123456789 (optional)"
                    value={form.maintenance_telegram_chat_id}
                    onChange={(e) => setForm({ ...form, maintenance_telegram_chat_id: e.target.value })}
                    data-testid="maintenance-chat-id-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Receives: AC issues, plumbing, electrical repairs
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="frontDeskChatId" className="flex items-center gap-2">
                    🛎️ Front Desk Chat ID
                  </Label>
                  <Input
                    id="frontDeskChatId"
                    placeholder="123456789 (optional)"
                    value={form.front_desk_telegram_chat_id}
                    onChange={(e) => setForm({ ...form, front_desk_telegram_chat_id: e.target.value })}
                    data-testid="front-desk-chat-id-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Receives: General guest requests and inquiries
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="managerChatId" className="flex items-center gap-2">
                    👔 Manager Chat ID
                  </Label>
                  <Input
                    id="managerChatId"
                    placeholder="123456789 (optional)"
                    value={form.manager_telegram_chat_id}
                    onChange={(e) => setForm({ ...form, manager_telegram_chat_id: e.target.value })}
                    data-testid="manager-chat-id-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Receives: Escalation alerts, overdue requests, guest complaints
                  </p>
                </div>
              </div>

              <Alert className="bg-primary/5 border-primary/20">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle className="text-sm">💡 Pro Tip</AlertTitle>
                <AlertDescription className="text-xs">
                  You can use the same chat ID for all departments, or create separate group chats for better organization. 
                  Commands: /ack REQ_ID to acknowledge, /done REQ_ID to complete.
                </AlertDescription>
              </Alert>
            </div>

            <div className="flex justify-between items-center pt-4">
              <Button variant="outline" onClick={() => setShowSetup(true)} size="sm">
                📖 Setup Guide
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={handleTestConnection} disabled={!form.chat_id}>
                  Test Connection
                </Button>
                <Button onClick={handleSave} disabled={saving} data-testid="save-telegram-settings-btn">
                  {saving ? 'Saving...' : 'Save Settings'}
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <Alert>
              <CheckCircle className="h-4 w-4" />
              <AlertTitle>Quick Setup Guide</AlertTitle>
              <AlertDescription>Follow these steps to connect your Telegram bot</AlertDescription>
            </Alert>

            <div className="space-y-4">
              <div className="space-y-2">
                <h4 className="font-semibold text-sm">Step 1: Create a Bot</h4>
                <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground">
                  <li>Open Telegram and search for <strong>@BotFather</strong></li>
                  <li>Send command: <code className="bg-muted px-1 rounded">/newbot</code></li>
                  <li>Follow instructions to name your bot</li>
                  <li>Copy the <strong>Bot Token</strong> provided</li>
                </ol>
              </div>

              <div className="space-y-2">
                <h4 className="font-semibold text-sm">Step 2: Get Your Chat ID</h4>
                <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground">
                  <li>Start a conversation with your new bot</li>
                  <li>Send any message (e.g., "hello")</li>
                  <li>Search for <strong>@userinfobot</strong> on Telegram</li>
                  <li>Send /start to get your Chat ID</li>
                </ol>
              </div>

              <div className="space-y-2">
                <h4 className="font-semibold text-sm">Step 3: Configure Webhook</h4>
                <p className="text-sm text-muted-foreground">
                  Your webhook URL (copy this):
                </p>
                <div className="bg-muted p-2 rounded font-mono text-xs break-all">
                  {window.location.origin}/api/availability/telegram/webhook/easystay-webhook-secret-2026
                </div>
              </div>

              <Alert>
                <ExternalLink className="h-4 w-4" />
                <AlertTitle className="text-sm">Need Help?</AlertTitle>
                <AlertDescription className="text-xs">
                  Visit the full setup guide in the documentation or contact support
                </AlertDescription>
              </Alert>
            </div>

            <Button variant="outline" onClick={() => setShowSetup(false)} className="w-full">
              Back to Settings
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
